import * as React from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Button from "@mui/material/Button";
import Header from "components/Header";
import { toast } from "react-toastify";
import { Box, Dialog, DialogTitle, DialogActions } from "@mui/material";
import { getAllExpenseType, deleteExpenseType } from "state/apis";
import AddExpenseType from "./AddExpense";
import moment from "moment";
import { InputBase } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch } from "react-redux";
import { getDefaultProperty } from "state";

const ExpenseType = () => {
    const [isLoading, setIsLoading] = React.useState(true);
    const [data, setData] = React.useState([]);
    const [totalData, setTotalData] = React.useState([]);
    const [openAddForm, setOpenAddForm] = React.useState(false);
    const [selectedRow, setSelectedRow] = React.useState(null);
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = React.useState(false);
    const [deleteItem, setDeleteItem] = React.useState(null);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(50);
    const [searchQuery, setSearchQuery] = React.useState("");
    const inputRef = React.useRef(null);
    const dispatch = useDispatch();
    const [role, setRole] = React.useState({});

    React.useEffect(() => {
        fetchData();
        fetchRole();
    }, [page, rowsPerPage, searchQuery]);

    const fetchRole = async () => {
        const res = await getDefaultProperty(dispatch);
        if (res?.data?.role && res?.data?.role?.permission) {
            setRole(JSON.parse(res?.data?.role?.permission));
        }
    };

    const fetchData = async () => {
        try {
            // setIsLoading(true);
            const res = await getAllExpenseType(rowsPerPage, page * rowsPerPage, searchQuery);
            setData(res?.data);
            setTotalData(res?.totalCount);
            setIsLoading(false);
        } catch (error) {
            console.error("Error fetching data:", error);
            toast.error("Failed to fetch data. Please try again.");
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const handleAddExpenseType = () => {
        setSelectedRow(null);
        setOpenAddForm(true);
    };

    const handleCloseAddForm = () => {
        setOpenAddForm(false);
    };

    const handleSubmitAddExpenseType = async () => {
        setOpenAddForm(false);
        setIsLoading(true);
        fetchData();
    };

    const handleEditClick = (row) => {
        setSelectedRow(row);
        setOpenAddForm(true);
    };

    const handleDeleteClick = (row) => {
        setDeleteItem(row?.id);
        setDeleteConfirmationOpen(true);
    };

    const handleDeleteConfirmation = async () => {
        try {
            await deleteExpenseType(deleteItem);
            toast.success("Expense type deleted successfully.");
            fetchData();
            setDeleteConfirmationOpen(false);
        } catch (error) {
            console.error("Error deleting expense type:", error);
            toast.error("Failed to delete expense type. Please try again.");
        }
    };
    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const handleSearch = () => {
        fetchData();
    };

    const focusInput = () => {
        inputRef.current.focus();
    };

    if (isLoading) {
        return (
            <Box sx={{ p: "24px 28px" }}>
                <Header title="Expense Type" />
                <Box sx={{ mt: 4, textAlign: "center", color: "#5a6a85" }}>Loading...</Box>
            </Box>
        );
    }

    return (
        <Box sx={{ p: "24px 28px" }}>
            <Header title="Expense Type" subtitle="List of Expense Type" />
            <Paper sx={{ width: "100%", overflow: "hidden", mt: 2.5, borderRadius: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                <Box display="flex" justifyContent="flex-end" p={2}>
                    <InputBase
                        placeholder="Search..."
                        sx={{
                            backgroundColor: "#eef1f6",
                            borderRadius: "8px",
                            px: 1.5,
                            py: 0.5,
                            fontSize: "0.8125rem",
                            width: 200,
                        }}
                        inputRef={inputRef}
                        value={searchQuery}
                        onChange={handleSearchChange}
                        onFocus={focusInput}
                    />
                    <IconButton onClick={handleSearch}>
                        <SearchIcon />
                    </IconButton>
                    {role?.expenseType?.add && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleAddExpenseType}
                        >
                            Add Expense Type
                        </Button>
                    )}
                </Box>
                <TableContainer sx={{ maxHeight: 900 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Created date</TableCell>
                                {(role?.expenseType?.delete || role?.expenseType?.update) && (
                                    <TableCell>Actions</TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.map((row) => (
                                <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                                    <TableCell>{row.name}</TableCell>
                                    <TableCell>
                                        {moment(row.createdAt).format("DD MMM YYYY")}
                                    </TableCell>
                                    {(role?.expenseType?.delete || role?.expenseType?.update) && (
                                        <TableCell>
                                            {role?.expenseType?.update && (
                                                <IconButton onClick={() => handleEditClick(row)}>
                                                    <EditIcon />
                                                </IconButton>
                                            )}
                                            {role?.expenseType?.delete && (
                                                <IconButton
                                                    color="error"
                                                    onClick={() => handleDeleteClick(row)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 100]}
                    component="div"
                    count={totalData}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
                <AddExpenseType
                    open={openAddForm}
                    onClose={handleCloseAddForm}
                    onSubmit={handleSubmitAddExpenseType}
                    editData={selectedRow}
                    editMode={!!selectedRow}
                />
                <Dialog
                    open={deleteConfirmationOpen}
                    onClose={() => setDeleteConfirmationOpen(false)}
                >
                    <DialogTitle fontWeight={"bold"}>Are you sure you want to delete?</DialogTitle>
                    <DialogActions>
                        <Button
                            onClick={() => setDeleteConfirmationOpen(false)}
                            sx={{ color: "#5a6a85" }}
                        >
                            Cancel
                        </Button>
                        <Button onClick={handleDeleteConfirmation} color="error" autoFocus>
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </Paper>
        </Box>
    );
};

export default ExpenseType;
